import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/design-system/ui/Icon';
import { Reveal } from '@/design-system/ui/Reveal';
import { useReducedMotion } from '@/core/theme/use-reduced-motion';
import { SITE_NAME } from '@/core/config/content';
import { useSEO } from '@/core/seo/useSEO';

const REASONS: { icon: IconName; title: string; body: string; node: { x: number; y: number } }[] = [
  { icon: 'shield', title: 'مدیریت بهتر', body: 'فرآیندی که پیش‌تر دستی و پراکنده بود، حالا از ثبت بار تا تحویل نهایی، دیجیتالی و قابل‌کنترل است.', node: { x: 60, y: 150 } },
  { icon: 'star', title: 'راحتی برای کاربر', body: 'مسیر از پیش رزرو می‌شود، قیمت شفاف است، و مرسوله لحظه‌به‌لحظه با کد رهگیری دنبال می‌شود.', node: { x: 400, y: 100 } },
  { icon: 'car', title: 'راحتی برای راننده', body: 'نوبت‌ها و وضعیت بار در اپ اختصاصی دیده می‌شود؛ جریان کاری منظم‌تر یعنی درآمد قابل‌پیش‌بینی‌تر.', node: { x: 740, y: 90 } },
];

const ROUTE = 'M 60 150 C 180 40, 280 40, 400 100 C 520 160, 620 160, 740 90';

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'چرا بلینو به‌وجود آمد',
  description: 'بلینو برای دیجیتالی‌سازی و مدیریت حمل‌ونقل بین‌شهری، به نفع مدیریت شرکت‌ها، راحتی مسافران و رانندگان شکل گرفت.',
  inLanguage: 'fa-IR',
  mainEntity: {
    '@type': 'Organization',
    name: SITE_NAME,
    description: 'بلینو (Belino) یک شرکت فناوری حمل‌ونقل بین‌شهری است که از سال ۱۴۰۳ فعالیت خود را با هدف دیجیتالی‌سازی عملیات شرکت‌ها و ایستگاه‌های حمل مسافر و بار آغاز کرده است.',
    slogan: 'حرکت، از اینجا شروع می‌شود',
  },
};

export default function WhyBlinoPage() {
  const still = useReducedMotion();
  useSEO({
    title: 'چرا بلینو؟',
    description: 'بلینو چیست و چرا به‌وجود آمد؟ نگاهی به مدیریت هوشمند، راحتی مسافر و راحتی راننده در حمل‌ونقل بین‌شهری.',
    canonical: '/why-blino',
    keywords: 'چرا بلینو, درباره بلینو, داستان بلینو, اکوسیستم بلینو, پلتفرم حمل و نقل بین شهری',
    jsonLd: JSON_LD,
  });

  const words = ['یک', 'مشکل', 'ساده،'];
  const accent = ['یک', 'راه‌حل', 'به‌هم‌پیوسته'];

  return (
    <div className="overflow-hidden">
      <section className="relative bg-[radial-gradient(120%_100%_at_50%_0%,rgb(0_168_150/0.1)_0%,transparent_60%)] pb-6 pt-6 md:pb-10 md:pt-10">
        <div className="pointer-events-none absolute -end-20 top-0 size-80 animate-aurora rounded-full bg-[radial-gradient(circle,rgb(2_195_154/0.22),transparent_70%)]" aria-hidden />
        <div className="pointer-events-none absolute -start-24 top-32 size-72 animate-aurora-slow rounded-full bg-[radial-gradient(circle,rgb(47_102_144/0.18),transparent_70%)]" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4">
          <Link to="/" className="group mb-8 inline-flex animate-fade-in items-center gap-1 text-sm font-bold text-ink-2 transition hover:text-navy">
            <Icon name="back" size={16} className="transition group-hover:translate-x-0.5" />
            بازگشت به صفحه اصلی
          </Link>
          <div className="mx-auto max-w-2xl text-center md:mx-0 md:text-start">
            <h1 className="mb-5 text-3xl font-bold leading-[1.4] sm:text-4xl md:text-5xl">
              {words.map((w, i) => (
                <span key={w + i} className="ms-[0.25em] inline-block animate-rise first:ms-0" style={{ animationDelay: `${i * 110}ms` }}>{w}</span>
              ))}{' '}
              <span className="inline-block bg-gradient-to-l from-action via-turquoise to-navy bg-clip-text text-transparent">
                {accent.map((w, i) => (
                  <span key={w + i} className="ms-[0.25em] inline-block animate-rise first:ms-0" style={{ animationDelay: `${380 + i * 120}ms` }}>{w}</span>
                ))}
              </span>
            </h1>
            <p className="animate-rise text-justify leading-loose text-ink-2 [animation-delay:760ms] md:text-lg">
              بلینو (Belino) از سال ۱۴۰۳ با یک ایده مشخص شروع به کار کرد: دیجیتالی‌سازی عملیات شرکت‌ها و ایستگاه‌های حمل مسافر و بار، به‌گونه‌ای که سفرها از قبل تعریف شوند و انتظار طولانی و بی‌برنامگی‌های سنتی کاهش یابد.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-14 md:pb-20" aria-label="سه دلیل بلینو">
        <Reveal>
          <svg viewBox="0 0 800 220" className="hidden h-auto w-full overflow-visible md:-mt-4 md:block" aria-hidden>
            <defs>
              <linearGradient id="route-gradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--color-navy)" />
                <stop offset="100%" stopColor="var(--color-turquoise)" />
              </linearGradient>
            </defs>
            <path d={ROUTE} fill="none" stroke="var(--color-line)" strokeWidth={8} strokeLinecap="round" opacity={0.6} />
            <path
              d={ROUTE}
              pathLength={1}
              fill="none"
              stroke="url(#route-gradient)"
              strokeWidth={3}
              strokeLinecap="round"
              strokeDasharray={1}
              className="animate-draw [animation-delay:300ms]"
              style={{ ['--len' as string]: 1, animationDuration: '2200ms' }}
            />
            {REASONS.map((r, i) => (
              <g key={r.title}>
                {!still && <circle cx={r.node.x} cy={r.node.y} r={7} fill="none" stroke="var(--color-mint)" strokeWidth={2} className="origin-center [transform-box:fill-box]" opacity={0}>
                  <animate attributeName="r" values="7;22" dur="2.4s" begin={`${1.2 + i * 0.7}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0" dur="2.4s" begin={`${1.2 + i * 0.7}s`} repeatCount="indefinite" />
                </circle>}
                <circle cx={r.node.x} cy={r.node.y} r={7} fill="var(--color-paper)" stroke="var(--color-turquoise)" strokeWidth={3} className="origin-center animate-pop [animation-fill-mode:both] [transform-box:fill-box]" style={{ animationDelay: `${500 + i * 700}ms` }} />
              </g>
            ))}
            {!still && (
              <circle r={5} fill="var(--color-mint)">
                <animateMotion dur="7s" begin="2.4s" repeatCount="indefinite" path={ROUTE} />
              </circle>
            )}
          </svg>
        </Reveal>
        <ul className="relative z-10 flex flex-col gap-6 md:-mt-12 md:flex-row">
          {REASONS.map((r, i) => (
            <Reveal as="li" key={r.title} delay={i * 140} className="group flex-1 rounded-[20px] border border-line bg-paper p-6 shadow-card transition duration-300 hover:-translate-y-1.5 hover:border-turquoise/40 hover:shadow-floating">
              <span className="mb-4 grid size-11 place-items-center rounded-md bg-primary-light text-navy transition duration-300 group-hover:-rotate-6 group-hover:scale-110 group-hover:bg-cta group-hover:text-white group-hover:shadow-glow"><Icon name={r.icon} size={22} /></span>
              <h2 className="mb-2 font-bold">{r.title}</h2>
              <p className="text-sm leading-loose text-ink-2">{r.body}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-solid to-navy-deep py-16 md:py-20">
        <div className="pointer-events-none absolute -end-16 -top-16 size-80 animate-aurora rounded-full bg-[radial-gradient(circle,rgb(2_195_154/0.3),transparent_70%)]" aria-hidden />
        <div className="pointer-events-none absolute -bottom-24 -start-10 size-72 animate-aurora-slow rounded-full bg-[radial-gradient(circle,rgb(251_191_36/0.12),transparent_70%)]" aria-hidden />
        <Reveal className="relative mx-auto max-w-xl px-4 text-center">
          <p className="text-shine mb-3 animate-sweep text-2xl font-bold md:text-3xl">«حرکت، از اینجا شروع میشه»</p>
          <p className="mb-8 leading-loose text-white/70">فلسفه‌ی برند بلینو، و همان چیزی که پشت هر تصمیم محصول ما ایستاده است.</p>
          <Link to="/magazine" className="btn-shine ring-pulse inline-flex h-12 items-center gap-2 rounded-md bg-cta px-6 font-bold text-white shadow-glow transition hover:-translate-y-px hover:brightness-105 active:scale-[0.97]">
            مطالعه مجله بلینو
            <Icon name="external" size={18} className="-scale-x-100" />
          </Link>
        </Reveal>
      </section>

      <section className="bg-fill py-14 md:py-16">
        <div className="mx-auto max-w-xl px-4">
          <Reveal className="mb-10 text-center">
            <h2 className="mb-2 text-xl font-bold md:text-2xl">مسیرهایی که بلینو در آن‌ها فعال است</h2>
            <p className="mx-auto max-w-md leading-loose text-ink-2">ایستگاه کرمان به‌عنوان مرکز شبکه، مسافر و بار را به مقصدهای بین‌شهری متصل می‌کند.</p>
          </Reveal>
          <div className="flex flex-col items-center">
            <Reveal delay={100}>
              <div className="ring-pulse rounded-full bg-hero px-8 py-3 font-bold text-white shadow-floating">ایستگاه کرمان</div>
            </Reveal>
            <Reveal delay={250}>
              <div className="h-9 w-0.5 origin-top animate-grow-y bg-gradient-to-b from-turquoise to-transparent [animation-delay:400ms]" aria-hidden />
            </Reveal>
            <ul className="flex w-full flex-col gap-4 sm:flex-row">
              {['سیرجان', 'رفسنجان'].map((c, i) => (
                <Reveal as="li" key={c} delay={400 + i * 150} className="flex-1 rounded-md border border-line bg-paper p-5 text-center transition duration-300 hover:-translate-y-1 hover:border-turquoise/40 hover:shadow-floating">
                  <p className="font-bold">{c}</p>
                  <p className="text-xs text-ink-2">تاکسی و بار بین‌شهری</p>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
